import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { isNode } from '../../global/helpers';
import {
    createElement,
    prerenderElementsTree,
    rebuildChildren,
} from '../../global/ui-helpers';
import { UIElement } from '../ui-element';
import type { ElementConfig } from '../ui-element.type';
import type {
    UIDynamicTableAbstractModifyRowPlugin,
    UIDynamicTableAbstractModifyTablePlugin,
    UIDynamicTableAbstractUITable,
    UIDynamicTableCompareFn,
    UIDynamicTableRecord,
} from './ui-dynamic-table.types';

import { UITable } from './ui-table';

/**
 * @class
 * @memberof SharedComponents
 * @abstract
 * @augments {UIElement}
 * @alias UIDynamicTable
 * @classdesc Represents a base abstract class for dynamic tables
 * @property {string} sortOrder {@attr sort-order}
 * @property {("asc"|"desc")} [sortDir="desc"] {@attr sort-dir}
 * @property {number} [limit] {@attr limit}
 * @property {string} [labelEmpty] {@attr empty-label}
 * @property {UITable} uiTable Table element
 * @property {HTMLTableSectionElement} tbody Table body
 * @property {Array<HTMLTableRowElement>} rows Array of table rows
 */
export class UIDynamicTable extends UIElement {
    @Attribute(String) sortOrder!: string;
    @Attribute(String) sortDir: 'asc' | 'desc' = 'desc';
    @Attribute(Number) limit!: number;
    @Attribute(String) labelEmpty!: string;

    @Child('ui-table') uiTable!: UITable;
    @Child('ui-table tbody') tbody!: HTMLTableSectionElement;
    @Child('ui-table tbody tr', true) rows!: Array<HTMLTableRowElement>;

    _records: Array<UIDynamicTableRecord> = [];

    /**
     * Return a unique key for given record.
     * The method to be overridden for descendants classes.
     * @param {object} record
     * @returns {string}
     */
    getUniqueKeyOf(record: UIDynamicTableRecord): string {
        return record.id;
    }

    /**
     * Build a comparison predicate for sorting.
     * The method to be overridden for descendants classes
     * @param {string} [key]
     * @returns {Function}
     */
    buildCompareFn(key?: string): UIDynamicTableCompareFn {
        return () => 0;
    }

    /**
     * Build a rendering config for ui-table.
     * The method to be overridden for descendants classes
     * @protected
     * @returns {ElementConfig}
     */
    protected buildTable(): ElementConfig {
        return {
            tagName: 'ui-table',
            attributes: {
                plugins: 'sortable invert',
            },
            children: [
                {
                    tagName: 'table',
                    children: [
                        {
                            tagName: 'thead',
                            children: [],
                        },
                        {
                            tagName: 'tbody',
                            children: [],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Build a rendering config for table row.
     * The method to be overridden for descendants classes
     * @param {object} record
     * @returns {ElementConfig}
     */
    buildTableRow(record: UIDynamicTableRecord): ElementConfig {
        return {
            tagName: 'tr',
            attributes: {},
            children: [],
        };
    }

    /**
     * Inserts records to the table. Render related table rows for each record.
     * @param {Array<object>} records
     * @returns {UIDynamicTable}
     */
    setRecords(records: Array<UIDynamicTableRecord>): UIDynamicTable {
        this._records = records;
        if (!records.length) {
            this.onEmptyResults();
            return this;
        }

        const entries = records.slice(0);
        const comparator = this.buildCompareFn();
        if (typeof comparator === 'function') {
            entries.sort(comparator);
        }

        rebuildChildren(
            this.tbody,
            entries
                .slice(0, this.limit > 0 ? this.limit : records.length)
                .map(this.renderTableRow.bind(this))
        );

        // if the table has invert plugin, then call its prepareLayout method
        const abstractUiTable = this.uiTable as UIDynamicTableAbstractUITable;
        if (typeof abstractUiTable.prepareLayout === 'function') {
            abstractUiTable.prepareLayout();
        }

        return this;
    }

    /**
     * Handle case when no records presented
     */
    onEmptyResults() {
        this.rebuildChildren.call(
            this.querySelector('tbody'),
            !this.labelEmpty
                ? []
                : [
                      {
                          tagName: 'tr',
                          children: [
                              {
                                  tagName: 'td',
                                  attributes: {
                                      colspan: String(
                                          this.querySelectorAll('thead th')
                                              .length
                                      ),
                                  },
                                  children: [
                                      {
                                          tagName: 'ui-placement',
                                          attributes: {
                                              align: 'center',
                                          },
                                          children: [
                                              {
                                                  tagName: 'strong',
                                                  children: [this.labelEmpty],
                                              },
                                          ],
                                      },
                                  ],
                              },
                          ],
                      },
                  ]
        );
    }

    /**
     * Update the table row for given record from table with same unique key
     * @param {object} record
     */
    updateRecord(record: UIDynamicTableRecord): void {
        const targetTr = this.querySelector(
            'tr[data-key="' + this.getUniqueKeyOf(record) + '"]'
        );
        if (targetTr) {
            targetTr.replaceWith(this.renderTableRow(record));
        }
    }

    /**
     * Remove the given record from table if a table row with same unique key is exist
     * @param {object} record
     */
    removeRecord(record: UIDynamicTableRecord): void {
        const targetTr = this.querySelector(
            'tr[data-key="' + this.getUniqueKeyOf(record) + '"]'
        );
        if (targetTr) {
            targetTr.parentElement?.removeChild(targetTr);
        }
    }

    /**
     * Add a new record if the table does not have a table row with the same unique key
     * @param {object} record
     */
    addRecord(record: UIDynamicTableRecord): void {
        const targetTr = this.querySelector('tr[data-key="' + record.id + '"]');
        if (!targetTr) {
            this.tbody.insertRow(0).replaceWith(this.renderTableRow(record));
        }
    }

    /**
     * Render a table row and applies the modifyRow method from connected plugins to the row
     * @param {object} record
     * @returns {HTMLTableRowElement}
     */
    renderTableRow(record: UIDynamicTableRecord): HTMLTableRowElement {
        const rowData = this.buildTableRow(record);
        const row = isNode(rowData)
            ? (rowData as Node as HTMLTableRowElement)
            : createElement<HTMLTableRowElement>(rowData)!;
        const plugins = this.getInstancePlugins();

        // look through every plugin and run modifyRow method if exists
        Object.values(plugins).forEach((plugin) => {
            const abstractPlugin =
                plugin as UIDynamicTableAbstractModifyRowPlugin;
            if (typeof abstractPlugin.modifyRow === 'function') {
                abstractPlugin.modifyRow(row, record);
            }
        });

        prerenderElementsTree(row, true);
        return row;
    }

    /**
     * Render a table and applies the modifyTable method from connected plugins to the table
     * @returns {UITable}
     */
    renderTable(): UITable {
        const tableData = this.buildTable();
        const table =
            tableData instanceof UITable
                ? tableData
                : createElement<UITable>(tableData)!;

        const plugins = this.getInstancePlugins();

        // look through every plugin and run modifyTable method if exists
        Object.values(plugins).forEach((plugin) => {
            const abstractPlugin =
                plugin as UIDynamicTableAbstractModifyTablePlugin;
            if (typeof abstractPlugin.modifyTable === 'function') {
                abstractPlugin.modifyTable(table);
            }
        });

        return table;
    }

    /**
     * Fires callback when related record is updated
     * @param {CustomEvent} event
     * @private
     */
    private handleRecordUpdated(event: CustomEvent<UIDynamicTableRecord>) {
        this.updateRecord(event.detail);
    }

    /**
     * Fires callback when related record is created
     * @param {CustomEvent} event
     * @private
     */
    private handleRecordCreated(event: CustomEvent<UIDynamicTableRecord>) {
        this.addRecord(event.detail);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.rebuildChildren([this.renderTable()]);
    }
}

UIDynamicTable.defineAbstractElement();
